<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.domain_configuration.tld')"
            :is-required="true"
            :error="errors.tld"
            :hint="__('settings.domain_configuration.tld_hint')"
        >
            <DInput
                id="tld"
                v-model="form.tld"
                :placeholder="__('settings.domain_configuration.tld')"
                :is-valid="isFormFieldValid('tld')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.domain_update_allowed')"
            :is-required="true"
            :error="errors.domain_update_allowed"
            :hint="__('settings.domain_configuration.domain_update_allowed_hint')"
        >
            <DSwitch
                id="domain_update_allowed"
                v-model="form.domain_update_allowed"
                color="success"
                variant="3d"
                inline
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.domain_update_price')"
            :is-required="true"
            :error="errors.domain_update_price"
            :hint="__('settings.domain_configuration.domain_update_price_hint')"
        >
            <DInput
                id="domain_update_price"
                v-model="form.domain_update_price"
                :placeholder="__('settings.domain_configuration.domain_update_price')"
                :is-valid="isFormFieldValid('domain_update_price')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.domain_trade_allowed')"
            :is-required="true"
            :error="errors.domain_trade_allowed"
            :hint="__('settings.domain_configuration.domain_trade_allowed_hint')"
        >
            <DSwitch
                id="domain_trade_allowed"
                v-model="form.domain_trade_allowed"
                color="success"
                variant="3d"
                inline
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.domain_trade_price')"
            :is-required="true"
            :error="errors.domain_trade_price"
            :hint="__('settings.domain_configuration.domain_trade_price_hint')"
        >
            <DInput
                id="domain_trade_price"
                v-model="form.domain_trade_price"
                :placeholder="__('settings.domain_configuration.domain_trade_price')"
                :is-valid="isFormFieldValid('domain_trade_price')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.lock_after_trade')"
            :is-required="true"
            :error="errors.lock_after_trade"
            :hint="__('settings.domain_configuration.lock_after_trade_hint')"
        >
            <DSwitch
                id="lock_after_trade"
                v-model="form.lock_after_trade"
                color="success"
                variant="3d"
                inline
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.dnssec_enabled')"
            :is-required="true"
            :error="errors.dnssec_enabled"
            :hint="__('settings.domain_configuration.dnssec_enabled_hint')"
        >
            <DSwitch
                id="dnssec_enabled"
                v-model="form.dnssec_enabled"
                color="success"
                variant="3d"
                inline
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.idn_character_sets')"
            :is-required="false"
            :error="errors.idn_character_sets"
            :hint="__('settings.domain_configuration.idn_character_sets_hint')"
        >
            <DMultiSelect
                id="idn_character_sets"
                :options="selectedIdnCharacterSetsOptions"
                :selection="true"
                :multiple="true"
                selection-type="tags"
                :search="true"
                @update="updateSelectedIdnCharacterSets"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.domain_configuration.additional_information')"
            :is-required="false"
            :error="errors.additional_information"
            :hint="__('settings.domain_configuration.additional_information_hint')"
        >
            <JsonForms
                :data="additionalInfo"
                :renderers="renderers"
                :schema="schema"
                :uischema="uischema"
                @change="onChange"
            />
            <DFormTextarea
                v-model="form.additional_information"
                :placeholder="__('settings.domain_configuration.additional_information')"
                :is-valid="isFormFieldValid('additional_information')"
                rows="10"
            />
        </DFormRow>
        <DFormControls
            :cancel-route="route('settings.domain-configuration.index')"
            :dirty="form.isDirty"
            @submit="submitForm"
        />
    </DForm>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed } from 'vue'
import { JsonForms } from '@jsonforms/vue'
import { vanillaRenderers } from '@jsonforms/vue-vanilla'
import { DArrayRenderer, DInputControlRenderer } from '~@/Components/JSONForms/renderers'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    domainConfiguration: {
        type: Object,
        default: () => {
            return {}
        }
    },
    errors: {
        type: Object,
        default: () => {
            return {}
        }
    },
    schema: {
        type: Object,
        default: () => ({}),
    },
    idnCharacterSets: {
        type: Array,
        default: () => []
    },
    selectedIdnCharacterSets: {
        type: Array,
        default: () => []
    }
})
const emit = defineEmits(['form-submitted'])

const renderers = Object.freeze([
    ...vanillaRenderers,
    DInputControlRenderer,
    DArrayRenderer,
])

const uischema = Object.freeze({
    type: 'ListWithDetail',
    scope: '#',
    initiallyExpanded: true,
    options: {
        getLabelForItem: (item) => __('settings.domain_configuration.additional_information_settings.field', {
            label: item?.name ?? __('settings.domain_configuration.additional_information_settings.new_field')
        }),
        detail: {
            type: 'VerticalLayout',
            elements: [
                { type: 'Control', scope: '#/properties/name' },
                { type: 'Control', scope: '#/properties/type' },
                { type: 'Control', scope: '#/properties/scope' },
                { type: 'Control', scope: '#/properties/value' },
                { type: 'Control', scope: '#/properties/value_expression', options: { type: 'textarea', rows: 4 } },
                { type: 'Control', scope: '#/properties/display_expression', options: { type: 'textarea', rows: 4 } },
                { type: 'Control', scope: '#/properties/read_only' },
                { type: 'Control', scope: '#/properties/disabled' },
                {
                    type: 'Control',
                    scope: '#/properties/options',
                    rule: {
                        effect: 'SHOW',
                        condition: {
                            scope: '#/properties/type',
                            schema: {
                                enum: ['select', 'radio']
                            }
                        }
                    }
                },
                {
                    type: 'Control',
                    scope: '#/properties/validation',
                    options: {
                        getLabelForItem: (item) => item
                            ? __('settings.domain_configuration.additional_information_settings.validation', {
                                rule: item,
                            })
                            : __('settings.domain_configuration.additional_information_settings.new_validation'),
                    }
                },
                { type: 'Control', scope: '#/properties/target' },
            ]
        }
    },
})

const form = useForm({
    tld: props.domainConfiguration?.tld ?? null,
    domain_update_allowed: props.domainConfiguration?.domain_update_allowed ?? false,
    domain_update_price: props.domainConfiguration?.domain_update_price ?? 0.0,
    domain_trade_allowed: props.domainConfiguration?.domain_trade_allowed ?? false,
    domain_trade_price: props.domainConfiguration?.domain_trade_price ?? 0.0,
    lock_after_trade: props.domainConfiguration?.lock_after_trade ?? false,
    dnssec_enabled: props.domainConfiguration?.dnssec_enabled ?? false,
    additional_information: props.domainConfiguration?.additional_information
        ? JSON.stringify(JSON.parse(props.domainConfiguration?.additional_information), null, 2) ?? null
        : null,
    idn_character_sets: props.selectedIdnCharacterSets ?? [],
})

const selectedIdnCharacterSetsOptions = computed(() => {
    return props.idnCharacterSets.map((option) => ({
        ...option,
        selected: form.idn_character_sets.map(String).includes(String(option.value))
    }))
})

function updateSelectedIdnCharacterSets (values) {
    form.idn_character_sets = values
}

const schema = computed(() => {
    return JSON.parse(props.schema.additional_information)
})

const additionalInfo = computed(() => {
    return props.domainConfiguration?.additional_information
        ? JSON.parse(props.domainConfiguration?.additional_information)
        : []
})

const submitForm = (event) => {
    // CoreUI's CMultiSelect renders its tag-remove button without a `type`, so it defaults to
    // `type="submit"` and fires a native form submit when a tag is removed. Ignore that submitter
    // so removing a character set does not trigger a save. The save button (DFormControls) and the
    // Enter key call this without such a submitter, so both keep working.
    if (event?.submitter?.classList?.contains('form-multi-select-tag-delete')) {
        return
    }

    emit('form-submitted', form)
}

const onChange = (event) => {
    form.additional_information = JSON.stringify(event.data, null, 2)
}
</script>
